/**
 * Transport-agnostic CSRF helpers shared by `apiRequest` (fetch) and the
 * forms-system XHR paths, so recovery policy cannot drift between transports.
 */
import localStorage from '../storage/localStorage';

export const CSRF_TOKEN_HEADER = 'X-CSRF-Token';
export const CSRF_TOKEN_STORAGE_KEY = 'csrfToken';

const INVALID_CSRF_TOKEN_DETAIL = 'Invalid Authenticity Token';
const ACCESS_TOKEN_EXPIRED_TEXT = 'token has expired';

/**
 * vets-api renders `errors` as either a bare string (`{ errors: 'Access token
 * has expired' }`) or a JSON:API array (`{ errors: [{ detail, status }] }`).
 * Normalize before matching: `Array.prototype.includes` tests element
 * equality, so a substring check against the array form returns false rather
 * than throwing, silently disabling the branch that depends on it.
 */
export const toErrorList = body =>
  Array.isArray(body?.errors) ? body.errors : [];

export const toErrorText = body =>
  typeof body?.errors === 'string' ? body.errors : '';

/** Parses a response body, returning null instead of throwing. */
export const safeParseJson = source => {
  try {
    return JSON.parse(source);
  } catch {
    return null;
  }
};

export const isInvalidCsrfTokenError = body =>
  toErrorList(body).some(
    error =>
      error?.status === '403' && error?.detail === INVALID_CSRF_TOKEN_DETAIL,
  );

export const isAccessTokenExpiredError = body =>
  toErrorText(body).includes(ACCESS_TOKEN_EXPIRED_TEXT);

export const getStoredCsrfToken = () =>
  localStorage.getItem(CSRF_TOKEN_STORAGE_KEY);

/**
 * Stores the refreshed token vets-api returns alongside a CSRF failure.
 * `getHeader` adapts the transport — `response.headers.get.bind(response)`
 * for fetch, `req.getResponseHeader.bind(req)` for XHR.
 *
 * @param {(name: string) => string|null} getHeader
 * @param {string} [previousToken] skips the write when the token is unchanged
 * @returns {string|null} the refreshed token, or null when none was returned
 */
export const applyFreshCsrfToken = (getHeader, previousToken) => {
  const token = getHeader(CSRF_TOKEN_HEADER);
  if (token && token !== previousToken) {
    localStorage.setItem(CSRF_TOKEN_STORAGE_KEY, token);
  }
  return token || null;
};
